import {
  MeterReadingsFilters,
  UtilityConfigFilters,
} from "@features/cx/shared/types/consumerRequest";
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { bulkBillingKeyFactory } from "@shared/api/queries/keyFactories/bx/BulkBilling/BulkBillCreate";
import { toast, useToast } from "@shared/hooks/use-toast";
import { downloadCSV, printPDFBlob } from "@shared/utils/utils";
import { useNavigate } from "react-router-dom";
import {
  approveBill,
  approveDiscoonnectionBill,
  disconnectionApi,
  downloadBillTemplate,
  getBillSummary,
  getMeterHistory,
  getMeterInfo,
  getWorkOrderDetails,
  refundRemainingAmount,
  rejectBill,
  submitConsumerSelection,
  updateDisconnection,
  postMasterAccountAction,
  refundSettlement,
} from "./api";
import {
  ConsumerBillFilters,
  ConsumerSearchFilters,
  ConsumerSupportDisconnectPayload,
  CreateBillGenerationPayload,
  CreateDisconnectionRequestPayload,
  DisconnectionFilters,
  SingleBillApprovePayload,
  UpdateBillGenerationPayload,
  UpdateDisconnectionRequestPayload,
} from "./types";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

// ================================
// CONSUMER SEARCH HOOKS
// ================================

/**
 * Search consumers for disconnection
 */
export const useConsumerSearch = (filters: ConsumerSearchFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.consumerSearch(filters),
    () => disconnectionApi.searchConsumer(filters),
    {
      enabled: filters.searchData.length >= 3,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
  );
};

/**
 * Get consumer meta data
 */
export const useConsumerMetaData = (params: {
  consumerId: number;
  remoteUtilityId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.consumerMetaData(params),
    () => disconnectionApi.getConsumerMetaData(params),
    {
      enabled: !!params.consumerId,
      staleTime: 5 * 60 * 1000,
    },
  );
};

/**
 * Get consumer bill data
 */
export const useConsumerBill = (filters: ConsumerBillFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.consumerBill(filters),
    () => disconnectionApi.getConsumerBill(filters),
    {
      enabled: !!filters.remoteUtilityId && !!filters.consumerId,
      staleTime: 2 * 60 * 1000,
    },
  );
};

/**
 * Get disconnection requests list
 */
export const useDisconnectionRequests = (filters: DisconnectionFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.list(filters),
    () => disconnectionApi.getDisconnectionRequests(filters),
    {
      staleTime: 2 * 60 * 1000,
    },
  );
};

export const useDisconnectionExport = (filters: DisconnectionFilters) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.exportList(filters), // Adjust this query key based on your factory structure
    async () => {
      setIsExporting(true);
      try {
        const blob =
          await disconnectionApi.exportDisconnectionRequests(filters); // Adjust API object name if needed

        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });

        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export disconnection requests: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    },
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};

/**
 * Get disconnection request detail
 */
export const useDisconnectionRequestDetail = (
  requestId: number,
  remoteUtilityId: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.details(String(requestId)),
    () =>
      disconnectionApi.getDisconnectionRequestDetail(
        requestId,
        remoteUtilityId,
      ),
    {
      enabled: !!requestId && !!remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    },
  );
};

/**
 * Get disconnection summary
 */
export const useDisconnectionSummary = (params: {
  remoteUtilityId: number;
  requestType: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.summary(params),
    () => disconnectionApi.getDisconnectionSummary(params),
    {
      enabled: !!params.remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    },
  );
};

// ================================
// DISCONNECTION MUTATION HOOKS
// ================================

/**
 * Create disconnection request
 */
export const useCreateDisconnectionRequest = () => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: CreateDisconnectionRequestPayload) =>
      disconnectionApi.createDisconnectionRequest(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.list(),
        QueryKeyFactory.module.cx.disconnectionRequests.summary(),
      ],
      onSuccess: (data: any, variables: any) => {
        queryClient.refetchQueries({
          queryKey: ["cx", "disconnectionRequests", "list"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "disconnectionRequests", "summary"],
        });
        toast({
          title: "Disconnection request created successfully!",
          variant: "default",
        });
        navigate("/cx/disconnections/success", {
          state: { ...data, formData: variables },
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to create disconnection request: ${error.message}`,
          variant: "destructive",
        });
      },
    },
  );
};

/**
 * Update disconnection request
 */
// export const useUpdateDisconnectionRequest = (requestId: number) => {
//   return useSmartMutation(
//     ({
//       requestId,
//       payload,
//     }: {
//       requestId: number;
//       payload: UpdateDisconnectionRequestPayload;
//     }) => disconnectionApi.updateDisconnectionRequest(requestId, payload),
//     {
//       invalidateKeys: [

//         QueryKeyFactory.module.cx.disconnectionRequests.details(
//           String(requestId)
//         ),
//         QueryKeyFactory.module.cx.disconnectionRequests.list(),
//         QueryKeyFactory.module.cx.disconnectionRequests.summary(),
//         QueryKeyFactory.module.cx.disconnectionRequests.details(String(requestId)),
//       ],
//       onSuccess: () => {
//         toast({
//           title: "Disconnection request updated successfully!",
//           variant: "default",
//         });
//       },
//       onError: (error: any) => {
//         toast({
//           title: `Failed to update disconnection request: ${error.message}`,
//           variant: "destructive",
//         });
//       },
//     }
//   );
// };

export const useUpdateDisconnectionRequest = (requestId: number) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) =>
      disconnectionApi.updateDisconnectionRequest(requestId, payload),
    {
      onSuccess: async () => {
        toast({
          title: "Success!",
          description: "Disconnection request updated successfully!",
        });

        // ✅ Refetch all related queries
        await queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.disconnectionRequests.details(
            String(requestId),
          ),
        });

        await queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.disconnectionRequests.list(),
        });

        await queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.disconnectionRequests.summary(),
        });

        await queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.disconnectionRequests.details(
            String(requestId),
          ),
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.message || "Failed to update disconnection request",
          variant: "destructive",
        });
      },
    },
  );
};

/**
 * Delete disconnection request
 */
export const useDeleteDisconnectionRequest = () => {
  return useSmartMutation(
    (requestId: number) =>
      disconnectionApi.deleteDisconnectionRequest(requestId),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.list(),
        QueryKeyFactory.module.cx.disconnectionRequests.summary(),
      ],
      onSuccess: () => {
        toast({
          title: "Disconnection request deleted successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to delete disconnection request: ${error.message}`,
          variant: "destructive",
        });
      },
    },
  );
};

// ================================
// BILL GENERATION HOOKS
// ================================

/**
 * Create bill generation
 */
export const useCreateBillGeneration = (requestId: number) => {
  return useSmartMutation(
    (payload: CreateBillGenerationPayload) =>
      disconnectionApi.createBillGeneration(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.consumerBill(),
        QueryKeyFactory.module.cx.disconnectionRequests.details(
          requestId?.toString(),
        ),
      ],
      onSuccess: () => {
        toast({
          title: "Bill generation created successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to create bill generation: ${error.message}`,
          variant: "destructive",
        });
      },
    },
  );
};

/**
 * Update bill generation
 */
export const useUpdateBillGeneration = (billId: number) => {
  return useSmartMutation(
    ({
      billId,
      payload,
    }: {
      billId: number;
      payload: UpdateBillGenerationPayload;
    }) => disconnectionApi.updateBillGeneration(billId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.consumerBill(),
        QueryKeyFactory.module.cx.disconnectionRequests.details(
          billId.toString(),
        ),
      ],
      onSuccess: () => {
        toast({
          title: "Bill generation updated successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to update bill generation: ${error.message}`,
          variant: "destructive",
        });
      },
    },
  );
};

/**
 * Approve single bill
 */
export const useApproveSingleBill = (requestId: number) => {
  return useSmartMutation(
    ({
      billId,
      payload,
    }: {
      billId: number;
      payload: SingleBillApprovePayload;
    }) => disconnectionApi.approveSingleBill(billId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.consumerBill(),
        QueryKeyFactory.module.cx.disconnectionRequests.details(
          requestId.toString(),
        ),
      ],
      onSuccess: () => {
        toast({
          title: "Bill approved successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to approve bill: ${error.message}`,
          variant: "destructive",
        });
      },
    },
  );
};

/**
 * Disconnect consumer support
 */
export const useDisconnectConsumerSupport = (requestId: number) => {
  return useSmartMutation(
    ({
      consumerId,
      payload,
    }: {
      consumerId: number;
      payload: ConsumerSupportDisconnectPayload;
    }) => disconnectionApi.disconnectConsumerSupport(consumerId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.details(
          requestId.toString(),
        ),
        QueryKeyFactory.module.cx.disconnectionRequests.list(),
        QueryKeyFactory.module.cx.disconnectionRequests.consumerBill(),
      ],
      onSuccess: () => {
        toast({
          title: "Consumer disconnected successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to disconnect consumer: ${error.message}`,
          variant: "destructive",
        });
      },
    },
  );
};

export const useDisconnectionStatus = (params: {
  remoteUtilityId: number;
  tab: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.status(
      params.remoteUtilityId,
      params.tab,
    ), // Include params in query key for caching
    () => disconnectionApi.getStatus(params),
  );
};

// Hook for setup progress (original)
export const useDisconnectionCategory = (params: {
  remoteUtilityId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.category(), // Include params in query key for caching
    () => disconnectionApi.getCategory(params),
  );
};

/**
 * Get utility request configuration
 */
export const useUtilityRequestConfiguration = (
  filters: UtilityConfigFilters,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.utilityConfig(filters),
    () => disconnectionApi.getUtilityRequestConfiguration(filters),
    {
      enabled: !!filters.requestType,
      staleTime: 10 * 60 * 1000,
    },
  );
};

/**
 * Get time slot choices
 */
export const useTimeSlotChoices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.timeSlotChoices(),
    () => disconnectionApi.getTimeSlotChoices(),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    },
  );
};

/**
 * Get time slot choices
 */
export const useBillTemplates = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.billTemplates(),
    () => disconnectionApi.getBillTemplates(remoteUtilityId),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    },
  );
};

export const useGetConsumerCsvTemplate = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const query = useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.csvTemplate(),
    async () => {
      setIsDownloading(true);
      try {
        const data = await disconnectionApi.getConsumerCsvTemplate();
        if (typeof data === "string" && data.startsWith("http")) {
          window.open(data, "_blank");
        } else {
          const blob = new Blob([data || ""], {
            type: "text/csv;charset=utf-8;",
          });
          downloadCSV(blob, "bulk_disconnection_template.csv");
        }
        return data;
      } catch (error: any) {
        toast({
          title: `Failed to download template: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsDownloading(false);
      }
    },
    {
      enabled: false,
    },
  );

  const downloadTemplate = () => {
    query.refetch();
  };

  return {
    downloadTemplate,
    isDownloading: isDownloading || query.isFetching,
  };
};

export const useValidateConsumerCsv = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartMutation(
    (file: File) =>
      disconnectionApi.validateConsumerCsv(file, Number(remoteUtilityId)),
    {
      onSuccess: () => {
        toast({
          title: "CSV validated successfully",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Validation failed: ${error.message}`,
          variant: "destructive",
        });
      },
    },
  );
};

export const useSearchConsumersInfo = (filters: {
  remoteUtilityId: number;
  requestType: string;
  page: number;
  limit: number;
  searchData?: string;
  category?: string;
  subCategory?: string;
  premise?: string;
  status?: string;
  accountType?: string;
  is_outstanding_info?: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.searchConsumersInfo(
      filters,
    ),
    () => disconnectionApi.searchConsumersInfo(filters),
  );
};

export const useCreateBulkRequest = () => {
  return useSmartMutation(
    (payload: any) => disconnectionApi.createBulkRequest(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Bulk disconnection request created successfully.",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "disconnectionRequests", "summary"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "disconnectionRequests", "list"],
        });
      },
      onError: (error: any) => {
        toast({
          title: `Error: ${error.message}`,
          variant: "destructive",
        });
      },
    },
  );
};

export const useMeterReadings = (filters: MeterReadingsFilters) => {
  const { startDate, endDate, meterId } = filters;

  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.meterReadings(filters),
    () => disconnectionApi.getMeterReadings(filters),
    {
      enabled: Boolean(startDate && endDate && meterId),
      staleTime: 30 * 60 * 1000, // 30 minutes
    },
  );
};

export const useBulkDisconnectDetails = (batchId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.bulkDisconnectDetails(
      batchId,
    ),
    () => disconnectionApi.bulkDisconnectDetails(batchId),
    {
      enabled: !!batchId,
    },
  );
};

export const useAcknowledgeBulkRequest = (batchId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useSmartMutation(
    () => disconnectionApi.acknowledgeBulkRequest(batchId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.cx.disconnectionRequests.bulkDisconnectDetails(
              batchId,
            ),
        });
        toast({
          title: "Success",
          description: "Bulk request acknowledged successfully.",
        });
      },
    },
  );
};

export const useApproveBulkRequest = (batchId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useSmartMutation(() => disconnectionApi.approveBulkRequest(batchId), {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey:
          QueryKeyFactory.module.cx.disconnectionRequests.bulkDisconnectDetails(
            batchId,
          ),
      });
      toast({
        title: "Success",
        description: "Bulk request approved successfully.",
      });
    },
  });
};

export const useCloseBulkRequest = (batchId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useSmartMutation(
    (payload?: any) => disconnectionApi.closeBulkRequest(batchId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.cx.disconnectionRequests.bulkDisconnectDetails(
              batchId,
            ),
        });
        toast({
          title: "Success",
          description: "Bulk request closed successfully.",
        });
      },
    },
  );
};

export const useRejectBulkRequest = (batchId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useSmartMutation(
    (payload: { rejection_reason: string }) =>
      disconnectionApi.rejectBulkRequest(batchId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.cx.disconnectionRequests.bulkDisconnectDetails(
              batchId,
            ),
        });
        toast({
          title: "Success",
          description: "Bulk request rejected successfully.",
        });
      },
    },
  );
};

export const useHoldBulkRequest = (batchId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useSmartMutation(() => disconnectionApi.holdBulkRequest(batchId), {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey:
          QueryKeyFactory.module.cx.disconnectionRequests.bulkDisconnectDetails(
            batchId,
          ),
      });
      toast({
        title: "Success",
        description: "Bulk request put on hold successfully.",
      });
    },
  });
};

export const useResumeBulkRequest = (batchId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useSmartMutation(() => disconnectionApi.resumeBulkRequest(batchId), {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey:
          QueryKeyFactory.module.cx.disconnectionRequests.bulkDisconnectDetails(
            batchId,
          ),
      });
      toast({
        title: "Success",
        description: "Bulk request resumed successfully.",
      });
    },
  });
};

export const useBulkRequestConsumersList = (params: {
  batchId: string;
  remoteUtilityId: number;
  page: number;
  limit: number;
  searchData?: string;
  category?: string;
  subCategory?: string;
  premise?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.bulkRequestConsumersList(
      params,
    ),
    () => disconnectionApi.getBulkRequestConsumersList(params),
    {
      enabled: !!params.batchId && !!params.remoteUtilityId,
    },
  );
};

export const useBulkRequestMeterList = (params: {
  batchId: string;
  remoteUtilityId: number;
  page: number;
  limit: number;
  searchData?: string;
  category?: string;
  subCategory?: string;
  accountType?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.bulkRequestMeterList(
      params,
    ),
    () => disconnectionApi.getBulkRequestMeterList(params),
    {
      enabled: !!params.batchId && !!params.remoteUtilityId,
    },
  );
};

export const useSubmitConsumerSelection = () => {
  const { toast } = useToast();
  return useSmartMutation(
    async (payload: any) => await submitConsumerSelection(payload),
    {
      onSuccess: async (_, payload) => {
        // ✅ Assuming requestId is available in the payload or response
        const requestId = payload?.requestId;

        if (requestId) {
          await queryClient.refetchQueries({
            queryKey: QueryKeyFactory.module.cx.disconnectionRequests.details(
              String(requestId),
            ),
          });
        }

        toast({
          title: "Success!",
          description: "Consumer selection submitted successfully.",
        });
      },

      onError: (error: any) => {
        toast({ title: "Error while creating the request" });
      },
    },
  );
};

export const usePrintBill = () => {
  return useSmartMutation(async (billId: string) => {
    const blob = await downloadBillTemplate(billId);
    printPDFBlob(blob);
    return blob;
  });
};

export const useApproveBill = (billId: any, billDraftId: number) => {
  const { toast } = useToast();

  return useSmartMutation(() => approveBill(billId, billDraftId), {
    onError: (error: any) => {
      toast({ title: "Error While Approving the Bill" });
    },
  });
};

export const useApproveDisconnectionBill = (billId: any, requestID: number) => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: Record<string, any>) =>
      approveDiscoonnectionBill(billId, requestID, payload),
    {
      onSuccess: () => {
        // ✅ Invalidate disconnection details query after approval
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.disconnectionRequests.details(
            String(requestID),
          ),
        });

        // ✅ Also invalidate consumerBill query without filters
        queryClient.invalidateQueries({
          queryKey: ["cx", "disconnectionRequests", "consumerBill"],
        });
      },
      onError: (error: any) => {
        toast({ title: "Error While Approving the Bill" });
      },
    },
  );
};

export const useRejectBill = (billId: any) => {
  const { toast } = useToast();

  return useSmartMutation(() => rejectBill(billId), {
    onError: (error: any) => {
      toast({ title: "Error While Approving the Bill" });
    },
  });
};

export const useUpdateDisconnection = (requestId: number) => {
  const navigate = useNavigate();
  return useSmartMutation(
    ({ requestId, payload }: { requestId: number; payload: any }) =>
      updateDisconnection(requestId, payload),

    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.details(
          String(requestId),
        ),
        QueryKeyFactory.module.cx.disconnectionRequests.list(),
        QueryKeyFactory.module.cx.disconnectionRequests.summary(),
      ],
      onSuccess: async () => {
        toast({ title: "Disconnection request updated successfully!" });
        queryClient.refetchQueries({
          queryKey: ["cx", "disconnectionRequests"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "disconnectionRequests", "list"],
        });
        await queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.disconnectionRequests
            .details(String(requestId))
            .slice(0, 3),
          exact: false,
        });
        navigate(`/cx/disconnections/?tab=history`);
      },
      onError: (error: any) => {
        toast({
          title: `Failed to update disconnection request: ${error.message}`,
        });
      },
    },
  );
};

export const useWorkOrderDetails = (search: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.workOrderDetails(search),
    () => getWorkOrderDetails(search),
    {
      enabled: !!search,
    },
  );
};

export const useMeterInfo = (deviceNo: string, utilityService: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.meterInfo(
      deviceNo,
      utilityService,
    ),
    () => getMeterInfo(deviceNo, utilityService),
    {
      enabled: !!deviceNo,
    },
  );
};

export const useMeterHistory = (consumerId: number, action: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.meterHistory(consumerId),
    () => getMeterHistory(consumerId),
    {
      enabled: !!consumerId && action === "history",
    },
  );
};

export const useRefundRemainingAmountBill = () => {
  return useSmartMutation(
    (payload: Record<string, any>) => refundRemainingAmount(payload),
    {
      onSuccess: () => {
        // ✅ Also invalidate consumerBill query without filters
        queryClient.invalidateQueries({
          queryKey: ["cx", "disconnectionRequests", "consumerBill"],
        });
      },
    },
  );
};

export const useDownloadMeterCSV = () => {
  return useSmartMutation(
    (batchId: string) => disconnectionApi.downloadMeterCSV(batchId),
    {
      onSuccess: (data, batchId) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `meter_readings_${batchId}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      },
    },
  );
};

export const useUploadMeterCSV = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useSmartMutation(
    ({ batchId, file }: { batchId: string; file: File }) =>
      disconnectionApi.uploadMeterCSV(batchId, file),
    {
      onSuccess: async (_, { batchId }) => {
        // Invalidate the meter list query to refresh data
        await queryClient.invalidateQueries({
          queryKey: ["cx", "disconnectionRequests", "bulkRequestMeterList"],
        });

        toast({
          title: "Success",
          description: "Meter readings CSV uploaded successfully.",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Upload Failed",
          description:
            error?.response?.data?.message ||
            "Failed to upload meter readings.",
          variant: "destructive",
        });
      },
    },
  );
};

export const useGenerateBulkBills = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useSmartMutation(
    ({ batchId, payload }: { batchId: string; payload: any }) =>
      disconnectionApi.generateBulkBills(batchId, payload),
    {
      onSuccess: (_, { batchId }) => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.cx.disconnectionRequests.bulkDisconnectDetails(
              batchId,
            ),
        });
        toast({
          title: "Success",
          description: "Bill generation request submitted successfully.",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Generation Failed",
          description: error?.message || "Failed to initiate bill generation.",
          variant: "destructive",
        });
      },
    },
  );
};

export const useBillConfigMeta = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.billConfigMeta(
      remoteUtilityId,
    ),
    () => disconnectionApi.getBillConfigMeta(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useTrackBillsProgress = (taskId: string | null) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.trackBillsProgress(
      taskId || "",
    ),
    () => disconnectionApi.trackBillsProgress(taskId!),
    {
      enabled: !!taskId,
      refetchInterval: (data: any) =>
        data?.completed || data?.progress >= 100 || data?.percentage >= 100
          ? false
          : 5000,
    },
  );
};
export const useGetTrackBillsProgress = (taskId: string | null) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.trackBillsProgress(
      taskId || "",
    ),
    () => disconnectionApi.trackBillsProgress(taskId!),
    {
      enabled: !!taskId,
    },
  );
};

/**
 * Get generated bills for a specific run ID
 */
export const useGeneratedBills = (filters: {
  run_id: string | number;
  remote_utility_id: number;
  page?: number;
  limit?: number;
}) => {
  return useSmartQuery(
    bulkBillingKeyFactory.billsList(
      filters.run_id,
      filters.page || 1,
      filters.limit || 50,
    ),
    () => disconnectionApi.getGeneratedBills(filters),
    {
      enabled: !!filters.run_id && !!filters.remote_utility_id,
      staleTime: 1 * 60 * 1000,
    },
  );
};

/**
 * Approve bulk bill run
 */
export const useApproveBulkBill = () => {
  return useSmartMutation((runId: string | number) =>
    disconnectionApi.approveBulkBill(runId),
  );
};

export const useOwnerTypeChoices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.ownerTypeChoices(),
    () => disconnectionApi.getOwnerTypeChoices(),
  );
};

export const useBulkRequestConsumersDownload = (batchId: string) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadMutation = useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.bulkRequestConsumersDownload(
      batchId,
    ),
    async () => {
      setIsDownloading(true);
      try {
        const blob =
          await disconnectionApi.downloadBulkRequestConsumersList(batchId);
        downloadCSV(blob, `bulk_request_consumers_${batchId}.csv`);
        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to download consumers list: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsDownloading(false);
      }
    },
    {
      enabled: false,
    },
  );

  const download = () => {
    downloadMutation.refetch();
  };

  return {
    download,
    isDownloading: isDownloading || downloadMutation.isFetching,
  };
};

export const useSaveMeterReading = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => disconnectionApi.saveSingleMeterReading(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Meter reading saved successfully.",
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "disconnectionRequests", "bulkRequestMeterList"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to save meter reading.",
          variant: "destructive",
        });
      },
    },
  );
};

export const useDownloadAllBulkRequestBills = (batchId: string) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { toast } = useToast();

  const downloadMutation = useSmartQuery(
    ["bx", "download-all-bill", batchId],
    async () => {
      setIsDownloading(true);
      try {
        const blob =
          await disconnectionApi.downloadAllBulkRequestBills(batchId);
        downloadCSV(blob, `bulk_bills_${batchId}.zip`);
        return { success: true };
      } catch (error: any) {
        toast({
          title: "Error",
          description: error?.message || "Failed to download bills.",
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsDownloading(false);
      }
    },
    {
      enabled: false,
    },
  );

  const download = () => {
    downloadMutation.refetch();
  };

  return {
    download,
    isDownloading: isDownloading || downloadMutation.isFetching,
  };
};

export const useBillSummary = (batchId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.billSummary(batchId),
    () => getBillSummary(batchId),
    {
      enabled: !!batchId,
    },
  );
};

export const useBillErrorsCsv = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { toast } = useToast();

  const downloadErrorsCsv = async (taskId: string) => {
    if (!taskId) return;
    setIsDownloading(true);
    try {
      const blob = await disconnectionApi.getBillErrorsCsv(taskId);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `bill_generation_errors_${taskId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({
        title: "Success",
        description: "Error log downloaded successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to download error log: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return { downloadErrorsCsv, isDownloading };
};
export const useRetryBills = () => {
  const { toast } = useToast();
  return useSmartMutation(
    ({ runId, payload }: { runId: number; payload: any }) =>
      disconnectionApi.retryBills(runId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Retry Initiated",
          description: "Attempting to regenerate failed bills...",
        });
      },
    },
  );
};

export const useMasterAccountAction = (requestId: number) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useSmartMutation(
    (payload: {
      master_account_action: "make_individual" | "move";
      new_master_account_id?: number;
    }) => postMasterAccountAction(requestId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Master account action confirmed successfully.",
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "disconnectionRequests", "details"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.message || "Failed to confirm master account action.",
          variant: "destructive",
        });
      },
    },
  );
};

export const useRefundSettlement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useSmartMutation(
    (payload: any) => refundSettlement(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Refund settlement processed successfully.",
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "disconnectionRequests", "details"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to process refund settlement.",
          variant: "destructive",
        });
      },
    },
  );
};
